// Admin Dashboard Tab Component
import React from 'react';
import { GroupBox } from '../shared/GroupBox';
import { Building2, Users, DollarSign, UserCheck, Package, Activity } from 'lucide-react';
import { format } from 'date-fns';
import { useAdmin } from './AdminContext';

export function AdminDashboardTab({ auditLogs = [] }) {
  const { stats } = useAdmin();

  return (
    <div className="space-y-4">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="p-4 rounded-lg border border-yellow-500/30 bg-yellow-900/10">
          <div className="flex items-center gap-2">
            <Building2 className="w-5 h-5 text-yellow-500" />
            <span className="text-sm text-gray-400">Total Tenants</span>
          </div>
          <div className="text-3xl font-bold text-yellow-500">{stats?.total_tenants || 0}</div>
          <div className="text-xs text-gray-500">{stats?.active_tenants || 0} active</div>
        </div>
        
        <div className="p-4 rounded-lg border border-blue-500/30 bg-blue-900/10">
          <div className="flex items-center gap-2">
            <Users className="w-5 h-5 text-blue-500" />
            <span className="text-sm text-gray-400">Total Users</span>
          </div>
          <div className="text-3xl font-bold text-blue-500">{stats?.total_users || 0}</div>
          <div className="text-xs text-gray-500">{stats?.active_sessions || 0} online</div>
        </div>
        
        <div className="p-4 rounded-lg border border-green-500/30 bg-green-900/10">
          <div className="flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-green-500" />
            <span className="text-sm text-gray-400">Total Revenue</span>
          </div>
          <div className="text-3xl font-bold text-green-500">₹{(stats?.total_revenue || 0).toLocaleString()}</div>
        </div>
        
        <div className="p-4 rounded-lg border border-purple-500/30 bg-purple-900/10">
          <div className="flex items-center gap-2">
            <UserCheck className="w-5 h-5 text-purple-500" />
            <span className="text-sm text-gray-400">New Signups (30d)</span>
          </div>
          <div className="text-3xl font-bold text-purple-500">{stats?.recent_signups || 0}</div>
        </div>
      </div>

      {/* Plan Distribution */}
      <GroupBox title="Plan Distribution" className="border-yellow-500/50">
        <div className="p-4">
          <div className="flex flex-wrap gap-4">
            {stats?.plan_distribution && Object.entries(stats.plan_distribution).map(([plan, count]) => (
              <div key={plan} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-800">
                <Package className="w-4 h-4 text-yellow-500" />
                <span className="capitalize">{plan}</span>
                <span className="font-bold text-yellow-500">{count}</span>
              </div>
            ))}
          </div>
        </div>
      </GroupBox>

      {/* Recent Activity */}
      <GroupBox title="Recent Activity" className="border-gray-700">
        <div className="p-4 max-h-64 overflow-y-auto">
          {auditLogs.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">No recent activity</p>
          ) : (
            auditLogs.slice(0, 10).map((log, idx) => (
              <div key={idx} className="flex items-center justify-between py-2 border-b border-gray-800 last:border-0">
                <div className="flex items-center gap-2">
                  <Activity className="w-4 h-4 text-gray-500" />
                  <span className="text-sm">{log.action}</span>
                </div>
                <span className="text-xs text-gray-500">
                  {format(new Date(log.timestamp), 'MMM d, HH:mm')}
                </span>
              </div>
            ))
          )}
        </div>
      </GroupBox>
    </div>
  );
}
